import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Settings } from "lucide-react";
import { useProjectQuotes } from "@/hooks/useQuotes";
import { useProjectInvoices } from "@/hooks/useInvoices";
import { useUpdateProject } from "@/hooks/useProjects";
import { useSyncedField } from "@/hooks/useRealtime";
import { QuotesListCard } from "./finance/QuotesListCard";
import { QuoteDetailView } from "./finance/QuoteDetailView";
import { InvoicesListCard } from "./finance/InvoicesListCard";
import { InvoiceDetailView } from "./finance/InvoiceDetailView";
import { TimeTrackingCard } from "./finance/TimeTrackingCard";
import { formatProjectName } from "@/lib/format-project-name";
import type { CustomerContact } from "@/types/finance-entities";

interface ProjectFinanceTabProps {
  project: {
    id: string;
    name: string;
    number?: string | null;
    language: string;
    conditions?: string | null;
    default_vat_rate?: string;
    default_discount_percent?: number;
    previous_edition_project_id?: string | null;
    start_date?: string | null;
    conditions?: string | null;
    event_start_date?: string | null;
    event_end_date?: string | null;
    planning_start_date?: string | null;
    planning_end_date?: string | null;
    companies?: {
      name: string;
      invoice_address: string | null;
      number: string | null;
    } | null;
    contact?: { first_name: string; last_name: string } | null;
    account_manager?: { first_name: string; last_name: string } | null;
  };
  selectedQuoteId?: string;
  selectedInvoiceId?: string;
  selectedVersionId?: string;
  basePath: string;
}

export function ProjectFinanceTab({
  project,
  selectedQuoteId,
  selectedInvoiceId,
  selectedVersionId,
  basePath,
}: ProjectFinanceTabProps) {
  const navigate = useNavigate();

  const navigateToQuote = useCallback(
    (quoteId: string) => navigate(`${basePath}/quotes/${quoteId}`),
    [navigate, basePath],
  );
  const navigateToQuoteVersion = useCallback(
    (quoteId: string, versionId: string | null) => {
      if (versionId) {
        navigate(`${basePath}/quotes/${quoteId}/versions/${versionId}`);
      } else {
        navigate(`${basePath}/quotes/${quoteId}`);
      }
    },
    [navigate, basePath],
  );
  const navigateToInvoice = useCallback(
    (invoiceId: string) => navigate(`${basePath}/invoices/${invoiceId}`),
    [navigate, basePath],
  );
  const navigateToFinance = useCallback(
    () => navigate(basePath),
    [navigate, basePath],
  );
  const { data: quotes = [], isLoading } = useProjectQuotes(project.id);
  const { data: invoices = [], isLoading: invoicesLoading } =
    useProjectInvoices(project.id);
  const updateProject = useUpdateProject();
  const projectDisplayName = formatProjectName(
    project.name,
    project.event_start_date,
  );

  const defaultVatRate =
    (project.default_vat_rate as "CH_8_1" | "NONE") || "CH_8_1";
  const defaultDiscountPercent = project.default_discount_percent ?? 0;

  const projectCustomerContact: CustomerContact = {
    customerName: project.companies?.name,
    customerAddress: project.companies?.invoice_address ?? undefined,
    customerReference: project.companies?.number ?? undefined,
    contactName: project.contact
      ? `${project.contact.first_name} ${project.contact.last_name}`
      : undefined,
    accountManagerName: project.account_manager
      ? `${project.account_manager.first_name} ${project.account_manager.last_name}`
      : undefined,
  };

  const discountField = useSyncedField({
    serverValue: String(defaultDiscountPercent),
    onSave: useCallback(
      (value: string) => {
        updateProject.mutate({
          id: project.id,
          updates: { default_discount_percent: parseFloat(value) || 0 },
        });
      },
      [project.id, updateProject],
    ),
  });

  if (selectedInvoiceId) {
    return (
      <InvoiceDetailView
        invoiceId={selectedInvoiceId}
        projectId={project.id}
        projectNumber={project.number ?? undefined}
        projectLanguage={project.language}
        eventStartDate={project.event_start_date ?? undefined}
        eventEndDate={project.event_end_date ?? undefined}
        planningStartDate={project.planning_start_date ?? undefined}
        planningEndDate={project.planning_end_date ?? undefined}
        customerContact={projectCustomerContact}
        onBack={navigateToFinance}
      />
    );
  }

  if (selectedQuoteId) {
    return (
      <QuoteDetailView
        quoteId={selectedQuoteId}
        selectedVersionId={selectedVersionId}
        basePath={`${basePath}/quotes/${selectedQuoteId}`}
        projectId={project.id}
        projectNumber={project.number ?? undefined}
        projectLanguage={project.language}
        projectConditions={project.conditions}
        projectDefaultVatRate={defaultVatRate}
        projectDefaultDiscountPercent={defaultDiscountPercent}
        eventStartDate={project.event_start_date ?? undefined}
        eventEndDate={project.event_end_date ?? undefined}
        planningStartDate={project.planning_start_date ?? undefined}
        planningEndDate={project.planning_end_date ?? undefined}
        customerContact={projectCustomerContact}
        onBack={navigateToFinance}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-tab">
      {/* Quote Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Quote Settings
          </CardTitle>
          <CardDescription>
            Default settings applied to new quote versions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
            <div className="space-y-2">
              <Label>Default VAT Rate</Label>
              <Select
                value={defaultVatRate}
                onValueChange={(value: "CH_8_1" | "NONE") => {
                  updateProject.mutate({
                    id: project.id,
                    updates: { default_vat_rate: value },
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CH_8_1">8.1% (Switzerland)</SelectItem>
                  <SelectItem value="NONE">No VAT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Default Discount %</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                {...discountField.inputProps}
                onFocus={(e) => e.target.select()}
                className="[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <QuotesListCard
          projectId={project.id}
          projectName={projectDisplayName}
          previousEditionProjectId={project.previous_edition_project_id ?? null}
          projectDates={project}
          quotes={quotes}
          isLoading={isLoading}
          onSelectQuote={navigateToQuote}
          onSelectVersion={navigateToQuoteVersion}
        />

        <TimeTrackingCard projectId={project.id} />

        <InvoicesListCard
          projectId={project.id}
          projectName={projectDisplayName}
          projectNumber={project.number || undefined}
          invoices={invoices}
          quotes={quotes}
          isLoading={invoicesLoading}
          projectDefaultVatRate={defaultVatRate}
          projectDefaultDiscountPercent={defaultDiscountPercent}
          onSelectInvoice={navigateToInvoice}
        />
      </div>
    </div>
  );
}
